// Tipuri pentru managementul utilizatorilor

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: UserRole;
  status: UserStatus;
  clinicId?: string;
  clinicName?: string;
  emailVerified: boolean;
  profileImage?: string;
  photoUrl?: string; // Pentru compatibilitate cu backend-ul
  createdAt: string;
  updatedAt: string;
}

export interface Veterinarian {
  id: string;
  userId: string;
  specializations: string[];
  description?: string;
  experienceYears: number;
  profilePhoto?: string;
  cvFile?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface VeterinarianFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  password: string;
  specializations: string[];
  description?: string;
  experienceYears: number;
  profilePhoto?: File;
  cvFile?: File;
}

export interface UserFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  role: UserRole;
  status: UserStatus;
  clinicId?: string;
  password?: string;
  // Campuri specifice veterinarului
  specializations?: string | null;
  experienceYears?: number | null;
  description?: string | null;
  profilePhoto?: string | null | File;
  cvFile?: string | null | File;
}

export enum UserRole {
  MASTER_ADMIN = 'master_admin',
  CLINIC_ADMIN = 'clinic_admin',
  VETERINARIAN = 'veterinarian',
  RECEPTIONIST = 'reception',
  ACCOUNTANT = 'accountant',
  CLIENT = 'client'
}

export enum UserStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PENDING = 'pending',
  SUSPENDED = 'suspended'
}

export interface UserFilters {
  role?: UserRole;
  status?: UserStatus;
  clinicId?: string;
  search?: string;
}

export interface UserActions {
  onCreate: (userData: UserFormData) => Promise<void>;
  onEdit: (userId: string, userData: Partial<UserFormData>) => Promise<void>;
  onActivate: (userId: string) => Promise<void>;
  onDeactivate: (userId: string) => Promise<void>;
  onResetPassword: (userId: string) => Promise<void>;
  onDelete?: (userId: string) => Promise<void>;
}

export interface VeterinarianActions {
  onCreate: (veterinarianData: VeterinarianFormData) => Promise<void>;
  onEdit: (veterinarianId: string, veterinarianData: Partial<VeterinarianFormData>) => Promise<void>;
  onUploadPhoto: (file: File) => Promise<string>;
  onUploadCv: (file: File) => Promise<string>;
}

export interface Permission {
  canCreateUsers: boolean;
  canEditUsers: boolean;
  canDeleteUsers: boolean;
  canActivateUsers: boolean;
  canResetPasswords: boolean;
  canManageVeterinarians: boolean;
  canViewAllClinics: boolean;
}

export interface RolePermissions {
  [UserRole.MASTER_ADMIN]: Permission;
  [UserRole.CLINIC_ADMIN]: Permission;
  [UserRole.VETERINARIAN]: Permission;
  [UserRole.RECEPTIONIST]: Permission;
  [UserRole.ACCOUNTANT]: Permission;
  [UserRole.CLIENT]: Permission;
}

// Constante pentru permisiuni
export const ROLE_PERMISSIONS: RolePermissions = {
  [UserRole.MASTER_ADMIN]: {
    canCreateUsers: true,
    canEditUsers: true,
    canDeleteUsers: true,
    canActivateUsers: true,
    canResetPasswords: true,
    canManageVeterinarians: true,
    canViewAllClinics: true,
  },
  [UserRole.CLINIC_ADMIN]: {
    canCreateUsers: true,
    canEditUsers: true,
    canDeleteUsers: false,
    canActivateUsers: true,
    canResetPasswords: true,
    canManageVeterinarians: true,
    canViewAllClinics: false,
  },
  [UserRole.VETERINARIAN]: {
    canCreateUsers: false,
    canEditUsers: false,
    canDeleteUsers: false,
    canActivateUsers: false,
    canResetPasswords: false,
    canManageVeterinarians: false,
    canViewAllClinics: false,
  },
  [UserRole.RECEPTIONIST]: {
    canCreateUsers: false,
    canEditUsers: false,
    canDeleteUsers: false,
    canActivateUsers: false,
    canResetPasswords: false,
    canManageVeterinarians: false,
    canViewAllClinics: false,
  },
  [UserRole.ACCOUNTANT]: {
    canCreateUsers: false,
    canEditUsers: false,
    canDeleteUsers: false,
    canActivateUsers: false,
    canResetPasswords: false,
    canManageVeterinarians: false,
    canViewAllClinics: false,
  },
  [UserRole.CLIENT]: {
    canCreateUsers: false,
    canEditUsers: false,
    canDeleteUsers: false,
    canActivateUsers: false,
    canResetPasswords: false,
    canManageVeterinarians: false,
    canViewAllClinics: false,
  },
};

// Helper functions pentru permisiuni
export const hasPermission = (userRole: UserRole, permission: keyof Permission): boolean => {
  return ROLE_PERMISSIONS[userRole][permission];
};

export const canManageUser = (currentUserRole: UserRole, targetUserRole: UserRole): boolean => {
  // Master admin poate gestiona pe toata lumea
  if (currentUserRole === UserRole.MASTER_ADMIN) return true;
  
  // Clinic admin poate gestiona doar utilizatorii din clinica sa (nu pe master admin)
  if (currentUserRole === UserRole.CLINIC_ADMIN) {
    return targetUserRole !== UserRole.MASTER_ADMIN;
  }
  
  return false;
};
